import { Component, DestroyRef, inject, OnDestroy, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { isEmpty } from 'lodash';
import { distinctUntilChanged, filter, skipWhile, take, timeout } from 'rxjs/operators';

import { selectAppConfigState } from 'src/app/store/app-config/app-config.selectors';
import { resetMapControls } from 'src/app/store/map-control/map-control.actions';

import { SiteService } from '../../../../core/api/site.service';
import { RedirectService } from '../../../../core/helper/redirect.service';
import { SiteHelperService } from '../../../../core/helper/site.service';
import { Site } from '../../../../shared/models';
import * as fromApp from '../../../../store';
import { selectCurrentUser } from '../../../../store/auth/auth.selectors';
import { toggleParcelSelectLock } from '../../../../store/parcel/parcel.actions';
import { updateSelectParcelSiteSuccess, updateSiteFailure } from '../../../../store/site/site.actions';
import { selectActiveSite, selectAvailableCohorts } from '../../../../store/site/site.selectors';

@Component({
  selector: 'fc-eligibility-check',
  templateUrl: './eligibility-check.component.html',
  styleUrls: ['./eligibility-check.component.scss'],
})
export class EligibilityCheckComponent implements OnInit, OnDestroy {
  destroyRef = inject(DestroyRef);

  showSorryMessage = false;
  eligibilityForm: UntypedFormGroup;
  loggedIn = false;
  contactEmail = '';
  contactPhone = '';
  helpEmail = '';
  helpPhone = '';
  activeSite: Site;
  siteId: number;
  numberOfBuyers = 0;

  constructor(
    private formBuilder: UntypedFormBuilder,
    readonly route: ActivatedRoute,
    readonly store: Store<fromApp.AppState>,
    readonly siteService: SiteService,
    readonly redirectService: RedirectService,
    readonly siteHelper: SiteHelperService,
    private router: Router
  ) {
    this.eligibilityForm = this.formBuilder.group({
      isOwner: [null, Validators.required],
      under5000Acres: [null, Validators.required],
    });
  }

  ngOnInit() {
    this.store.dispatch(toggleParcelSelectLock({ lock: true }));
    this.store
      .select(selectAvailableCohorts)
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe(cohorts => {
        const buyers = new Set();
        cohorts.forEach(cohort => {
          buyers.add(cohort.buyer.id);
        });
        this.numberOfBuyers = buyers.size;
      });
    this.store
      .select(selectActiveSite)
      .pipe(
        filter(site => !isEmpty(site)),
        distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(activeSite => {
        this.activeSite = activeSite;
        this.siteId = activeSite.id;
      });

    this.store
      .select(selectCurrentUser)
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe(currentUser => {
        this.loggedIn = currentUser !== null;
      });

    this.store
      .select(selectAppConfigState)
      .pipe(
        timeout(10000),
        skipWhile(appConfig => appConfig.config.length === 0),
        take(1),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(appConfig => {
        this.contactEmail = appConfig.config.fc_contact_email;
        this.contactPhone = appConfig.config.fc_contact_phone;
        this.helpEmail = appConfig.config.fc_help_email;
        this.helpPhone = appConfig.config.fc_help_phone;
      });
  }

  ngOnDestroy(): void {
    this.store.dispatch(toggleParcelSelectLock({ lock: false }));
  }

  onClickContinue(): void {
    if (this.isOwner && this.under5000Acres) {
      this.saveAndNavigateToMap();
    } else {
      this.showSorryMessage = true;
    }
  }

  saveAndNavigateToMap(): void {
    this.siteService
      .updateSiteParcels(this.activeSite.id, this.activeSite.versions[0].parcels)
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe(
        site => {
          this.eligibilityForm.markAsPristine();
          this.store.dispatch(resetMapControls());
          this.store.dispatch(updateSelectParcelSiteSuccess({ data: site }));
        },
        error => this.store.dispatch(updateSiteFailure({ error }))
      );
  }

  goBack(exitParcelSelection: boolean = false): void {
    this.eligibilityForm.markAsPristine();
    const url = exitParcelSelection
      ? '/site/new-site/step-three'
      : `/site/${this.siteId}/locate-property/select-parcels`;

    this.router.navigate([url]);
  }

  goToRoot(): void {
    this.redirectService.redirectToDashboard();
  }

  get isOwner(): boolean {
    return this.eligibilityForm.get('isOwner').value;
  }

  get under5000Acres(): boolean {
    return this.eligibilityForm.get('under5000Acres').value;
  }

  get isFormDirty(): boolean {
    return this.eligibilityForm.dirty;
  }
}
